import React from 'react'
import { ModalLayout } from '@/components/HeadlessUI'
import { MODAL_CONFIRM_PANEL_CLASS, MODAL_PLACEMENT_CENTER } from '@/constants'
import { useTranslation } from 'react-i18next'

export interface DeleteConfirmationProps {
	isOpen: boolean
	onClose: () => void
	onConfirm: () => void | Promise<void>
	title?: string
	description?: string
	confirmButtonText?: string
	cancelButtonText?: string
	iconClassName?: string
	confirmButtonIconClassName?: string
	confirming?: boolean
}

const DeleteConfirmation: React.FC<DeleteConfirmationProps> = ({
	isOpen,
	onClose,
	onConfirm,
	title,
	description,
	confirmButtonText,
	cancelButtonText,
	iconClassName = 'ri-delete-bin-7-line',
	confirmButtonIconClassName = 'ri-delete-bin-line',
	confirming = false,
}) => {
	const { t } = useTranslation()

	const resolvedTitle = title ?? t('Manage.Helper.Delete_Title', 'Delete item')
	const resolvedDescription =
		description ?? t('Manage.Helper.Delete_Description', 'Are you sure you want to delete this? This action cannot be undone.')
	const resolvedConfirm = confirmButtonText ?? t('Manage.Helper.Delete_Confirm', 'Delete')
	const resolvedCancel = cancelButtonText ?? t('Manage.Helper.Delete_Cancel', 'Cancel')

	const handleConfirm = async () => {
		if (confirming) return
		await onConfirm()
	}

	return (
		<ModalLayout showModal={isOpen} toggleModal={onClose} panelClassName={MODAL_CONFIRM_PANEL_CLASS} placement={MODAL_PLACEMENT_CENTER}>
			<div className="delete-confirmation" role="alertdialog" aria-labelledby="delete-confirmation-title" aria-describedby="delete-confirmation-desc">
				<div className="delete-confirmation__header">
					<div className="delete-confirmation__icon" aria-hidden>
						<i className={iconClassName} />
					</div>
					<h4 id="delete-confirmation-title" className="delete-confirmation__title">
						{resolvedTitle}
					</h4>
				</div>
				<div className="delete-confirmation__body">
					<p id="delete-confirmation-desc" className="delete-confirmation__description">
						{resolvedDescription}
					</p>
					<div className="delete-confirmation__actions">
						<button type="button" className="btn btn-secondary" onClick={onClose} disabled={confirming}>
							{resolvedCancel}
						</button>
						<button type="button" className="btn btn-danger" onClick={handleConfirm} disabled={confirming}>
							{confirming ? (
								<>
									<i className="ri-loader-4-line animate-spin" aria-hidden />
									{t('Manage.Helper.Delete_InProgress', 'Deleting…')}
								</>
							) : (
								<>
									<i className={confirmButtonIconClassName} aria-hidden />
									{resolvedConfirm}
								</>
							)}
						</button>
					</div>
				</div>
			</div>
		</ModalLayout>
	)
}

export default DeleteConfirmation
